import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Save, Settings, Webhook, TestTube, Radio, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export const SettingsTab = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Settings state
  const [n8nEndpoint, setN8nEndpoint] = useState('');
  const [testMode, setTestMode] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('key, value');

      if (error) throw error;

      // Map settings to state
      data?.forEach(setting => {
        if (setting.key === 'n8n_webhook_url') {
          setN8nEndpoint(setting.value);
        } else if (setting.key === 'whatsapp_test_mode') {
          setTestMode(setting.value === 'true');
        }
      });
    } catch (error: any) {
      toast({
        title: "Erro ao carregar configurações",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEndpoint = async () => {
    if (!n8nEndpoint.trim()) {
      toast({
        title: "Endpoint obrigatório",
        description: "Por favor, informe o endpoint do N8N",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('admin_settings')
        .update({ 
          value: n8nEndpoint.trim(),
          updated_at: new Date().toISOString(),
          updated_by: user?.id
        })
        .eq('key', 'n8n_webhook_url');

      if (error) throw error;

      toast({
        title: "Endpoint salvo",
        description: "O endpoint do N8N foi atualizado com sucesso",
      });
    } catch (error: any) {
      toast({
        title: "Erro ao salvar",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleTestMode = async (checked: boolean) => {
    setTestMode(checked);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('admin_settings')
        .update({ 
          value: checked ? 'true' : 'false',
          updated_at: new Date().toISOString(),
          updated_by: user?.id
        })
        .eq('key', 'whatsapp_test_mode');

      if (error) throw error;

      toast({
        title: checked ? "Modo Teste ativado" : "Modo Live ativado",
        description: checked 
          ? "Campanhas serão enviadas para apenas 1 consulente" 
          : "Campanhas serão enviadas para todos os consulentes ativos",
      });
    } catch (error: any) {
      toast({
        title: "Erro ao atualizar modo",
        description: error.message,
        variant: "destructive",
      });
      // Revert on error
      setTestMode(!checked);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* N8N Integration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl font-display text-primary">
            <Webhook className="w-5 h-5" />
            Integração N8N
          </CardTitle>
          <CardDescription>
            Configure o endpoint do webhook para campanhas WhatsApp
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="n8n-endpoint">Endpoint WhatsApp</Label>
            <Input
              id="n8n-endpoint"
              type="url"
              value={n8nEndpoint}
              onChange={(e) => setN8nEndpoint(e.target.value)}
              placeholder="https://webhook.exemplo.com/webhook/..."
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              URL do webhook N8N que receberá as requisições de envio de WhatsApp
            </p>
          </div>
          <Button onClick={handleSaveEndpoint} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Salvar Endpoint
          </Button>
        </CardContent>
      </Card>

      {/* Test/Live Mode */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl font-display text-primary">
            <Settings className="w-5 h-5" />
            Modo de Operação
          </CardTitle>
          <CardDescription>
            Alterne entre modo de teste e modo de produção para campanhas WhatsApp
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${testMode ? 'bg-amber-100 text-amber-600' : 'bg-green-100 text-green-600'}`}>
                {testMode ? <TestTube className="w-6 h-6" /> : <Radio className="w-6 h-6" />}
              </div>
              <div>
                <h3 className="font-semibold text-lg">
                  {testMode ? '🧪 Modo Teste' : '🚀 Modo Live'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {testMode 
                    ? 'Apenas o primeiro consulente receberá a campanha' 
                    : 'Todos os consulentes ativos receberão a campanha'}
                </p>
              </div>
            </div>
            <Switch
              checked={!testMode}
              onCheckedChange={(checked) => handleToggleTestMode(!checked)}
            />
          </div>

          {testMode && (
            <Alert className="border-amber-200 bg-amber-50">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Modo Teste ativo:</strong> Ao enviar uma campanha, apenas 1 mensagem será enviada 
                para validar o fluxo. Desative o modo teste para enviar para todos os consulentes.
              </AlertDescription>
            </Alert>
          )}

          {!testMode && (
            <Alert className="border-green-200 bg-green-50">
              <Radio className="w-4 h-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <strong>Modo Live ativo:</strong> Campanhas serão enviadas para todos os consulentes ativos. 
                Certifique-se de que a campanha está pronta antes de enviar.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsTab;
